import React, { useCallback } from 'react';
import classNames from 'classnames';
import { useField } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import SegmentedToggle from 'components/SegmentedToggle';
import {
  FILES_AVAILABILITY_MODE,
  FilesAvailabilityMode,
} from 'models/WorkflowConfiguration';
import {
  confirmShareEverythingAtOnce,
  confirmShareOneAtATime,
} from 'modules/releaseOrderConfirm';
import WorkflowGuideAnchor from '../WorkflowGuideAnchor';
import { FILES_REACH_SELECTOR } from '../../data';

import './FilesReachSelector.styles.scss';

type FilesReachSelectorProps = {
  className?: string;
  // While loading, the saved mode and lead counts are not known yet.
  disabled?: boolean;
  partWayLeadsCount?: number;
  leadsWithFilesCount?: number;
};

// Set per deal, but saved with the stage like the rest of the draft.
const FilesReachSelector: React.FC<FilesReachSelectorProps> = (props) => {
  const { className, disabled, partWayLeadsCount, leadsWithFilesCount } = props;

  const {
    input: { value, onChange },
    meta: { initial },
  } = useField<FilesAvailabilityMode>('filesAvailabilityMode');

  const { t } = useTranslation();

  const classes = classNames('files-reach-selector', className);

  // Going back to the saved mode only undoes the draft, so it is not asked.
  // Nor is a switch no lead would notice, like one before any file is shared.
  const handleChange = useCallback(
    async (mode: FilesAvailabilityMode) => {
      if (mode === value) return;

      if (mode !== initial) {
        const isEverythingAtOnce = mode === FILES_AVAILABILITY_MODE.ALL_AT_ONCE;
        const affectedLeadsCount = isEverythingAtOnce
          ? partWayLeadsCount
          : leadsWithFilesCount;

        if (affectedLeadsCount) {
          const shouldSwitch = await (isEverythingAtOnce
            ? confirmShareEverythingAtOnce()
            : confirmShareOneAtATime());

          if (!shouldSwitch) return;
        }
      }

      onChange(mode);
    },
    [initial, leadsWithFilesCount, onChange, partWayLeadsCount, value],
  );

  return (
    <div className={classes}>
      <p className="wft-title">
        {t('filesLeadsReach')}
        <WorkflowGuideAnchor
          id={FILES_REACH_SELECTOR}
          title="chooseHowLeadsGetFiles"
          description="chooseHowLeadsGetFilesDescription"
        />
      </p>

      <SegmentedToggle
        className="files-reach-selector__toggle"
        options={[
          {
            label: t('everythingAtOnce'),
            value: FILES_AVAILABILITY_MODE.ALL_AT_ONCE,
            disabled,
          },
          {
            label: t('oneAtATime'),
            value: FILES_AVAILABILITY_MODE.ONE_AT_A_TIME,
            disabled,
          },
        ]}
        selectedValue={value}
        onChange={(mode) => handleChange(mode as FilesAvailabilityMode)}
      />

      <p className="files-reach-selector__note">
        {t('appliesToAllFourStages')}
      </p>
    </div>
  );
};

export default FilesReachSelector;
